import { Box, Flex, IconButton, Image, Text } from "@chakra-ui/react";
import fileIcon from "assets/img/customer/file-icon.svg";
import { DownloadIcon } from "@chakra-ui/icons";
import { memo, useMemo } from "react";

type Props = {
  messageId: string;
  attachments: any[];
  getDisplayName: (url: string, att?: any) => string;
  onDownload: (url: string, displayName: string) => void;
};

export const MessageAttachments = memo(function MessageAttachments({
  messageId,
  attachments,
  getDisplayName,
  onDownload,
}: Props) {
  const items = useMemo(() => {
    return (attachments ?? [])
      .map((att: any, idx: number) => {
        const url =
          typeof att === "string" ? att : String(att?.fileUrl ?? att?.url ?? att?.href ?? "");
        if (!url) return null;

        const displayName = getDisplayName(url, att);
        return { url, displayName, idx };
      })
      .filter(Boolean) as Array<{ url: string; displayName: string; idx: number }>;
  }, [attachments, getDisplayName]);

  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  return (
    <Box mt="10px">
      {items.map(({ url, displayName, idx }) => (
        <Box key={`${messageId}-att-${idx}`} display="block" mt={idx === 0 ? 0 : "10px"}>
          <Flex
            align="center"
            justify="space-between"
            p="10px"
            bg="#FFF"
            borderRadius="12px"
            border="1px solid #D2DAE5"
          >
            <Flex minW="0" alignItems="center" gap="8px">
              <Image src={fileIcon} alt="file" boxSize="20px" flexShrink={0} />
              <Text
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                fontSize="14px"
                fontWeight="500"
                color="#0B0E10"
                noOfLines={1}
                _hover={{ textDecoration: "none" }}
              >
                {displayName}
              </Text>
            </Flex>

            <IconButton
              aria-label="Download"
              icon={<DownloadIcon />}
              variant="ghost"
              size="sm"
              color="#2F80ED"
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              _focus={{ boxShadow: "none" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDownload(url, displayName);
              }}
            />
          </Flex>
        </Box>
      ))}
    </Box>
  );
});
